import { useCallback, useSyncExternalStore } from "react";
import { create } from "zustand";
import {
  type ImportStore,
  createImportStore,
  selectIsImportPending,
} from "./file-to-storydoc-generation.store";

export const MAX_PARALLEL_IMPORTS = 3;
export const CAP_MESSAGE = `You have ${MAX_PARALLEL_IMPORTS} imports in progress. Finish or cancel one to start another.`;

export type ImportEntry = { id: string; store: ImportStore };

type RegistryState = {
  imports: ImportEntry[];
  viewingId: string | null;
  ownerId: string | null;
};

type RegistryActions = {
  addImport: () => ImportEntry | null;
  removeImport: (id: string) => void;
  setViewingId: (id: string | null) => void;
  bindOwner: (userId: string | null) => void;
  reset: () => void;
};

export const countActiveImports = (imports: ImportEntry[]) =>
  imports.filter((i) => i.store.getState().status !== "idle").length;

const newImportId = () =>
  `imp-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const revokeUpload = (store: ImportStore) => {
  for (const p of store.getState().upload?.pages ?? []) {
    if (p.thumbnailUrl.startsWith("blob:")) URL.revokeObjectURL(p.thumbnailUrl);
  }
};

export const useImportRegistry = create<RegistryState & RegistryActions>(
  (set, get) => ({
    imports: [],
    viewingId: null,
    ownerId: null,
    addImport: () => {
      if (countActiveImports(get().imports) >= MAX_PARALLEL_IMPORTS)
        return null;
      const entry: ImportEntry = {
        id: newImportId(),
        store: createImportStore(),
      };
      set((s) => ({ imports: [...s.imports, entry] }));
      return entry;
    },
    removeImport: (id) => {
      const entry = get().imports.find((i) => i.id === id);
      if (!entry) return;
      revokeUpload(entry.store);
      set((s) => ({
        imports: s.imports.filter((i) => i.id !== id),
        viewingId: s.viewingId === id ? null : s.viewingId,
      }));
    },
    setViewingId: (viewingId) => set({ viewingId }),
    bindOwner: (userId) => {
      if (userId === get().ownerId) return;
      for (const entry of get().imports) revokeUpload(entry.store);
      set({ imports: [], viewingId: null, ownerId: userId });
    },
    reset: () => set({ imports: [], viewingId: null, ownerId: null }),
  }),
);

export const selectViewing = (s: RegistryState): ImportEntry | null =>
  s.imports.find((i) => i.id === s.viewingId) ?? null;

export const selectAtCap = (s: Pick<RegistryState, "imports">) =>
  countActiveImports(s.imports) >= MAX_PARALLEL_IMPORTS;

export const getViewingImport = () =>
  selectViewing(useImportRegistry.getState());

export const findImportByStore = (store: ImportStore) =>
  useImportRegistry.getState().imports.find((i) => i.store === store) ?? null;

// Thumbnails of an upload that became an import belong to that import until it ends.
export const isUploadOwnedByImport = (pdfUrl: string | null) =>
  pdfUrl != null &&
  useImportRegistry
    .getState()
    .imports.some((i) => i.store.getState().upload?.pdfUrl === pdfUrl);

const useImportsSnapshot = <T>(read: (imports: ImportEntry[]) => T): T => {
  const imports = useImportRegistry((s) => s.imports);
  const subscribe = useCallback(
    (onChange: () => void) => {
      const unsubscribe = imports.map((i) => i.store.subscribe(onChange));
      return () => unsubscribe.forEach((u) => u());
    },
    [imports],
  );
  const getSnapshot = useCallback(() => read(imports), [imports, read]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

const hasPending = (imports: ImportEntry[]) =>
  imports.some((i) => selectIsImportPending(i.store.getState()));
const atCap = (imports: ImportEntry[]) =>
  countActiveImports(imports) >= MAX_PARALLEL_IMPORTS;

export const useHasPendingImport = () => useImportsSnapshot(hasPending);
export const useImportsAtCap = () => useImportsSnapshot(atCap);
